import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupAccount = () => {
  usePageMeta(PAGE_META.setupAccount);

  const navigate = useNavigate();
  const [accountInfo, setAccountInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) { setError('Invalid setup link'); setLoading(false); return; }
    fetch(`${API}/auth/setup-account/${token}`)
      .then(res => { if (!res.ok) throw new Error('Invalid'); return res.json(); })
      .then(data => { setAccountInfo(data); setLoading(false); })
      .catch(() => { setError('This setup link is invalid or has already been used.'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/setup-account`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ token, password })
      });
      if (response.ok) { setSuccess(true); }
      else { const data = await response.json(); toast.error(data.detail || 'Setup failed'); }
    } catch (err) { toast.error('Something went wrong'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <p>Loading...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <div className={shell.inner}>
            <div className={shell.card}>
              <div className={shell.head}>
                <div className={`${shell.statusIcon} ${shell.statusError}`}>
                  <Scale size={32} aria-hidden="true" />
                </div>
                <h1 className={shell.title}>Setup Link Invalid</h1>
                <p className={shell.subtitle}>{error}</p>
              </div>
              <button type="button" className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                onClick={() => navigate('/login')} data-testid="setup-go-login">
                Go to Login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (success) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <div className={shell.inner}>
            <div className={shell.card}>
              <div className={shell.head}>
                <div className={shell.statusIcon}>
                  <CheckCircle size={32} aria-hidden="true" />
                </div>
                <h1 className={shell.title}>Account Setup Complete!</h1>
                <p className={shell.subtitle}>
                  Your password has been set. You can now log in to your MyCaseLoan account.
                </p>
              </div>
              <button type="button" className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                onClick={() => navigate('/login')} data-testid="setup-login-btn">
                Log In
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const roleLabel = { client: 'Client', lawyer: 'Attorney', funder: 'Funder', admin: 'Administrator' }[accountInfo.role] || accountInfo.role;

  return (
    <div className={shell.page} data-testid="setup-account-page">
      <header className={shell.header}>
        <div className={shell.headerInner}>
          <button type="button" className={shell.logo} onClick={() => navigate('/')} aria-label="MyCaseLoan home">
            <Scale size={26} aria-hidden="true" />
            <span className={shell.logoText}>MyCaseLoan</span>
          </button>
        </div>
      </header>

      <main className={shell.centre}>
        <div className={shell.inner}>
          <div className={shell.card}>
            <div className={shell.head}>
              <div className={shell.mark}>
                <Scale size={28} aria-hidden="true" />
              </div>
              <h1 className={shell.title}>Set Up Your Account</h1>
              <p className={shell.subtitle}>
                Welcome, <strong>{accountInfo.name}</strong>! Create a password to complete your{' '}
                <strong>{roleLabel}</strong> account setup.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={formStyles.field}>
                <label className={formStyles.label} htmlFor="setup-email">Email</label>
                <input id="setup-email" className={formStyles.control} value={accountInfo.email} disabled />
              </div>
              <div className={formStyles.field}>
                <label className={formStyles.label} htmlFor="setup-password">Password *</label>
                <div className={formStyles.passwordWrap}>
                  <input id="setup-password" className={formStyles.control}
                    type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 characters" data-testid="setup-password" />
                  <button type="button" className={formStyles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className={formStyles.field}>
                <label className={formStyles.label} htmlFor="setup-confirm">Confirm Password *</label>
                <input id="setup-confirm" className={formStyles.control}
                  type={showPassword ? 'text' : 'password'} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password" data-testid="setup-confirm-password" />
              </div>
              <button type="submit" className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                disabled={submitting} data-testid="setup-submit-btn">
                {submitting ? 'Setting Up...' : 'Complete Setup'}
              </button>
            </form>
          </div>
          <p className={shell.footNote}>
            &copy; {new Date().getFullYear()} MyCaseLoan. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default SetupAccount;
